import type { AIModel } from '@/types/ai';

type ImageInputReference = NonNullable<
  NonNullable<AIModel['capabilities']>['inputReference']
>['image'];
type VideoInputReference = NonNullable<
  NonNullable<AIModel['capabilities']>['inputReference']
>['video'];
type AudioInputReference = NonNullable<
  NonNullable<AIModel['capabilities']>['inputReference']
>['audio'];

export interface NormalizedReferenceImageLimits {
  supported: boolean;
  totalMin?: number;
  totalMax: number;
  formats: string[];
  maxSizeMb?: number;
  minDimension?: number;
  maxPixels?: number;
  maxDimensionPx?: number;
  aspectRatioMin?: number;
  aspectRatioMax?: number;
}

export interface NormalizedReferenceVideoLimits {
  supported: boolean;
  totalMin?: number;
  totalMax: number;
  formats: string[];
  maxSizeMb?: number;
  minDurationS?: number;
  maxDurationS?: number;
  totalMaxDurationS?: number;
  minDimension?: number;
  maxDimension?: number;
  minPixels?: number;
  maxPixels?: number;
  aspectRatioMin?: number;
  aspectRatioMax?: number;
  fpsMin?: number;
  fpsMax?: number;
}

export interface NormalizedReferenceAudioLimits {
  supported: boolean;
  totalMin?: number;
  totalMax: number;
  formats: string[];
  maxSizeMb?: number;
  minDurationS?: number;
  maxDurationS?: number;
  totalMaxDurationS?: number;
}

export interface NormalizedInputReferenceLimits {
  supported: boolean;
  combinedMax?: number;
  image: NormalizedReferenceImageLimits;
  video: NormalizedReferenceVideoLimits;
  audio: NormalizedReferenceAudioLimits;
}

/** 与后端 model_validator._MODE_INPUT_REFERENCE_CONTRACTS 对齐 */
const MODE_INPUT_REFERENCE_CONTRACTS: Record<string, Record<string, Record<string, number>>> = {
  first_frame: { image: { totalMin: 1, totalMax: 1 } },
  first_last_frame: { image: { totalMin: 1, totalMax: 2 } },
};

type JsonRecord = Record<string, unknown>;

function asRecord(value: unknown): JsonRecord | undefined {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as JsonRecord)
    : undefined;
}

/** first_frame → firstFrame（匹配后端 format_definition_capabilities 的 deep_snake_to_camel） */
export function toCamelCaseModeKey(mode: string): string {
  return mode.replace(/_([a-z])/g, (_, char: string) => char.toUpperCase());
}

/**
 * capabilities.byMode[mode]。
 *
 * mode 名必须同时按原样和 camelCase 查：后端 `deep_snake_to_camel` 会递归转 key，
 * 线上拿到的是 `byMode.videoEdit`，只查 `byMode.video_edit` 会静默落空，
 * 让所有多词 mode（video_edit / video_extend / first_frame …）的覆写全部失效。
 */
function modeOverrides(capabilities: JsonRecord | undefined, mode: string | null | undefined) {
  const byMode = asRecord(capabilities?.byMode) ?? asRecord(capabilities?.by_mode);
  if (!byMode) return undefined;
  const key = mode ?? '';
  return asRecord(byMode[key]) ?? asRecord(byMode[toCamelCaseModeKey(key)]);
}

/**
 * 素材级：出现即白名单（与后端 resolve_input_reference 语义对齐）。
 * capabilities 使用前端 camelCase（inputReference / byMode），兼容 snake_case。
 */
export function resolveInputReference(
  capabilities: JsonRecord | undefined,
  mode?: string | null
): JsonRecord {
  const raw =
    asRecord(capabilities?.inputReference) ?? asRecord(capabilities?.input_reference) ?? {};

  let base: JsonRecord = {};
  for (const [key, value] of Object.entries(raw)) {
    if (key === 'byMode' || key === 'by_mode') continue;
    base[key] = asRecord(value) ? { ...asRecord(value) } : value;
  }

  const contract = MODE_INPUT_REFERENCE_CONTRACTS[mode ?? ''];
  if (contract) {
    const next: JsonRecord = {};
    for (const [key, value] of Object.entries(base)) {
      if (!asRecord(value)) next[key] = value;
    }
    for (const [key, patch] of Object.entries(contract)) {
      next[key] = { ...(asRecord(base[key]) ?? {}), ...patch };
    }
    base = next;
  }

  const overrideBag =
    modeOverrides(capabilities, mode)?.inputReference ??
    modeOverrides(capabilities, mode)?.input_reference;
  const override = asRecord(overrideBag);
  if (!override) return base;

  const result: JsonRecord = {};
  for (const [key, value] of Object.entries(base)) {
    if (!asRecord(value)) result[key] = value;
  }
  for (const [key, patch] of Object.entries(override)) {
    const patchObj = asRecord(patch);
    if (patchObj) {
      result[key] = { ...(asRecord(base[key]) ?? {}), ...patchObj };
    } else {
      result[key] = patch;
    }
  }
  return result;
}

/** 参数级：parameters[name] ⊕ byMode[mode][name] 键级覆写 */
export function resolveParameter(
  parameters: JsonRecord | undefined,
  name: string,
  capabilities: JsonRecord | undefined,
  mode?: string | null
): JsonRecord {
  const base = { ...(asRecord(parameters?.[name]) ?? {}) };
  const patch = asRecord(modeOverrides(capabilities, mode)?.[name]);
  if (patch) Object.assign(base, patch);
  return base;
}

function normalizePositiveNumber(value: unknown): number | undefined {
  const raw = Number(value);
  return Number.isFinite(raw) && raw > 0 ? raw : undefined;
}

function normalizeNonNegativeInteger(value: unknown): number {
  const raw = Number(value);
  return Number.isFinite(raw) && raw >= 0 ? Math.floor(raw) : 0;
}

function normalizeOptionalNonNegativeInteger(value: unknown): number | undefined {
  if (value === undefined || value === null) return undefined;
  return normalizeNonNegativeInteger(value);
}

function normalizeFormats(values: unknown): string[] {
  if (!Array.isArray(values)) return [];
  return values
    .map((item) => (typeof item === 'string' ? item.trim().toLowerCase() : ''))
    .filter((item) => item.length > 0);
}

function normalizeImageLimits(
  image?: ImageInputReference | JsonRecord
): NormalizedReferenceImageLimits {
  const rec = asRecord(image) ?? {};
  const totalMax = normalizeNonNegativeInteger(rec.totalMax ?? rec.total_max);
  const supported = rec.supported === true || (rec.supported !== false && totalMax > 0);
  return {
    supported,
    totalMin: normalizeOptionalNonNegativeInteger(rec.totalMin ?? rec.total_min),
    totalMax,
    formats: normalizeFormats(rec.formats),
    maxSizeMb: normalizePositiveNumber(rec.maxSizeMb ?? rec.max_size_mb),
    minDimension: normalizePositiveNumber(rec.minDimension ?? rec.min_dimension),
    maxPixels: normalizePositiveNumber(rec.maxPixels ?? rec.max_pixels),
    maxDimensionPx: normalizePositiveNumber(
      rec.maxDimensionPx ?? rec.max_dimension_px ?? rec.maxDimension ?? rec.max_dimension
    ),
    aspectRatioMin: normalizePositiveNumber(rec.aspectRatioMin ?? rec.aspect_ratio_min),
    aspectRatioMax: normalizePositiveNumber(rec.aspectRatioMax ?? rec.aspect_ratio_max),
  };
}

function normalizeVideoLimits(
  video?: VideoInputReference | JsonRecord
): NormalizedReferenceVideoLimits {
  const rec = asRecord(video) ?? {};
  const totalMax = normalizeNonNegativeInteger(rec.totalMax ?? rec.total_max);
  const supported = rec.supported === true || (rec.supported !== false && totalMax > 0);
  return {
    supported,
    totalMin: normalizeOptionalNonNegativeInteger(rec.totalMin ?? rec.total_min),
    totalMax,
    formats: normalizeFormats(rec.formats),
    maxSizeMb: normalizePositiveNumber(rec.maxSizeMb ?? rec.max_size_mb),
    minDurationS: normalizePositiveNumber(rec.minDurationS ?? rec.min_duration_s),
    maxDurationS: normalizePositiveNumber(
      rec.maxDurationS ?? rec.max_duration_s ?? rec.maxDuration ?? rec.max_duration
    ),
    totalMaxDurationS: normalizePositiveNumber(rec.totalMaxDurationS ?? rec.total_max_duration_s),
    minDimension: normalizePositiveNumber(rec.minDimension ?? rec.min_dimension),
    maxDimension: normalizePositiveNumber(rec.maxDimension ?? rec.max_dimension),
    minPixels: normalizePositiveNumber(rec.minPixels ?? rec.min_pixels),
    maxPixels: normalizePositiveNumber(rec.maxPixels ?? rec.max_pixels),
    aspectRatioMin: normalizePositiveNumber(rec.aspectRatioMin ?? rec.aspect_ratio_min),
    aspectRatioMax: normalizePositiveNumber(rec.aspectRatioMax ?? rec.aspect_ratio_max),
    fpsMin: normalizePositiveNumber(rec.fpsMin ?? rec.fps_min),
    fpsMax: normalizePositiveNumber(rec.fpsMax ?? rec.fps_max),
  };
}

function normalizeAudioLimits(
  audio?: AudioInputReference | JsonRecord
): NormalizedReferenceAudioLimits {
  const rec = asRecord(audio) ?? {};
  const totalMax = normalizeNonNegativeInteger(rec.totalMax ?? rec.total_max);
  const supported = rec.supported === true || (rec.supported !== false && totalMax > 0);
  return {
    supported,
    totalMin: normalizeOptionalNonNegativeInteger(rec.totalMin ?? rec.total_min),
    totalMax,
    formats: normalizeFormats(rec.formats),
    maxSizeMb: normalizePositiveNumber(rec.maxSizeMb ?? rec.max_size_mb),
    minDurationS: normalizePositiveNumber(rec.minDurationS ?? rec.min_duration_s),
    maxDurationS: normalizePositiveNumber(rec.maxDurationS ?? rec.max_duration_s),
    totalMaxDurationS: normalizePositiveNumber(rec.totalMaxDurationS ?? rec.total_max_duration_s),
  };
}

const UNSUPPORTED_IMAGE: NormalizedReferenceImageLimits = {
  supported: false,
  totalMax: 0,
  formats: [],
};
const UNSUPPORTED_VIDEO: NormalizedReferenceVideoLimits = {
  supported: false,
  totalMax: 0,
  formats: [],
};
const UNSUPPORTED_AUDIO: NormalizedReferenceAudioLimits = {
  supported: false,
  totalMax: 0,
  formats: [],
};

/**
 * @param mode 传入时按 capabilities.byMode 解析素材白名单（与后端一致）
 */
export function normalizeInputReferenceLimits(
  model?: AIModel,
  mode?: string | null
): NormalizedInputReferenceLimits {
  const capabilities = asRecord(model?.capabilities);
  const resolved = resolveInputReference(capabilities, mode);
  const whitelistActive =
    Boolean(mode) &&
    Boolean(
      asRecord(modeOverrides(capabilities, mode)?.inputReference) ??
      asRecord(modeOverrides(capabilities, mode)?.input_reference)
    );

  const imageRaw = asRecord(resolved.image);
  const videoRaw = asRecord(resolved.video);
  const audioRaw = asRecord(resolved.audio);

  return {
    supported: resolved.supported === true || Boolean(imageRaw || videoRaw || audioRaw),
    combinedMax: normalizePositiveNumber(resolved.combinedMax ?? resolved.combined_max),
    image: whitelistActive && !imageRaw ? UNSUPPORTED_IMAGE : normalizeImageLimits(imageRaw),
    video: whitelistActive && !videoRaw ? UNSUPPORTED_VIDEO : normalizeVideoLimits(videoRaw),
    audio: whitelistActive && !audioRaw ? UNSUPPORTED_AUDIO : normalizeAudioLimits(audioRaw),
  };
}
